import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, ArrowRight, Package as PackageIcon, Moon } from "lucide-react";
import { api, money } from "@/lib/api";
import { SearchWidget } from "@/components/SearchWidget";
import { Skeleton } from "@/components/ui/skeleton";

export default function Packages() {
  const [params] = useSearchParams();
  const destination = params.get("destination") || "";
  const [loading, setLoading] = useState(true);
  const [pkgs, setPkgs] = useState([]);

  useEffect(() => {
    setLoading(true);
    api.get("/packages", { params: destination ? { destination } : {} }).then(({ data }) => setPkgs(data.results)).finally(() => setLoading(false));
  }, [destination]);

  return (
    <div>
      <div className="bg-gradient-to-br from-brand to-[#FF0080] grain relative pt-8 pb-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto"><SearchWidget defaultTab="packages" /></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-14 relative">
        <h1 className="font-heading font-black text-2xl text-white mb-6">{destination ? `Holidays in ${destination}` : "All holiday packages"}</h1>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-96 rounded-2xl" />) :
            pkgs.length === 0 ? (
              <div className="col-span-full bg-white rounded-2xl border border-slate-200 p-12 text-center">
                <PackageIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No packages found for this destination.</p>
              </div>
            ) : pkgs.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} data-testid={`package-card-${i}`}>
                <Link to={`/packages/${p.id}`} className="group block rounded-2xl overflow-hidden border border-slate-200 bg-white hover:-translate-y-1 hover:shadow-2xl transition-all duration-300">
                  <div className="relative h-56 overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 to-transparent" />
                    <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-coral text-white text-xs font-bold">{p.tag}</span>
                    <div className="absolute bottom-4 left-4 text-white">
                      <div className="text-xs font-bold uppercase tracking-wide opacity-80">{p.country}</div>
                      <div className="font-heading font-extrabold text-2xl">{p.destination}</div>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-1 text-xs font-bold text-emerald-600 mb-2"><Star className="w-3.5 h-3.5 fill-emerald-500 text-emerald-500" />{p.rating} · {p.reviews_count} reviews</div>
                    <h3 className="font-heading font-bold text-xl text-slate-950 leading-snug">{p.title}</h3>
                    <div className="flex items-center gap-1.5 text-sm text-slate-500 mt-2"><Moon className="w-4 h-4" />{p.nights} nights · flights + hotel</div>
                    <div className="mt-5 pt-4 border-t border-slate-100 flex items-end justify-between">
                      <div><span className="text-xs text-slate-400">from</span><div className="font-heading font-black text-2xl text-slate-950">{money(p.price)}<span className="text-sm font-normal text-slate-400"> pp</span></div></div>
                      <span className="text-brand font-bold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">View deal <ArrowRight className="w-4 h-4" /></span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
        </div>
      </div>
    </div>
  );
}
